import React, { useRef } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * The template's advanced code: CSS added after the generated styles, and JavaScript run in the
 * statement page before it is laid out (in the PDF and in the preview). HTML goes into HTML elements
 * on the Design tab.
 */

interface CodeBoxProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}

/** A monospace text box: Tab inserts two spaces (Esc then Tab leaves it), the line count shows below. */
export const CodeBox: React.FC<CodeBoxProps> = ({ label, value, onChange, placeholder, rows = 18 }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const escaped = useRef(false);
  const lines = value ? value.split('\n').length : 0;
  const id = `code-${label.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <Box>
      <Typography component="label" htmlFor={id} variant="subtitle2" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
      <textarea
        id={id}
        ref={ref}
        value={value}
        rows={rows}
        spellCheck={false}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { escaped.current = true; return; }
          if (e.key === 'Tab' && !e.shiftKey && !escaped.current && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault();
            const el = e.currentTarget;
            const start = el.selectionStart;
            const end = el.selectionEnd;
            const next = `${value.slice(0, start)}  ${value.slice(end)}`;
            onChange(next);
            requestAnimationFrame(() => {
              if (ref.current) ref.current.selectionStart = ref.current.selectionEnd = start + 2;
            });
            return;
          }
          escaped.current = false;
        }}
        style={{
          width: '100%', boxSizing: 'border-box', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 12.5, lineHeight: 1.5, padding: 10, border: '1px solid #c4c9d0', borderRadius: 4, resize: 'vertical',
          tabSize: 2, background: '#fbfcfd', color: '#1f2328',
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {lines} line{lines === 1 ? '' : 's'} · {value.length.toLocaleString()} of 100,000 characters · Tab indents (Esc, then Tab, moves on)
      </Typography>
    </Box>
  );
};

const CSS_HELP = [
  ['.soa-page', 'one printed page (.soa-last = the last one)'],
  ['.soa-band-pageHeader, -reportHeader, -reportFooter, -pageFooter', 'the bands'],
  ['.soa-el, .soa-el-text / -field / -image / -line / -box / -kv / -html', 'the elements; .soa-in is their text'],
  ['[data-el="<element id>"]', 'one element (its id is under its properties)'],
  ['.soa-table, .soa-th, .soa-td, .soa-cell', 'the transaction table, headings, cells, cell text'],
  ['.soa-row-opening, .soa-row-tx, .soa-row-closing, .soa-row-empty', 'the table rows by kind'],
  ['.soa-kv, .soa-kv-l, .soa-kv-v', 'label / value tables'],
  ['.soa-ph', 'a page number'],
];

const JS_HELP = [
  ["soa.on('statement', function (st) { … })", 'before each statement is laid out: change st.rows, or add st.custom.x and print it with {{custom.x}}'],
  ["soa.on('page', function (pageEl, info) { … })", 'after numbering; info = {statement, number, count, docPage, docPages}'],
  ["soa.on('done', function (result) { … })", 'at the end; result = {pages, warnings, errors}'],
  ['soa.format.amount(n, decimals)', "an amount as the template prints it, e.g. '1,234.500'"],
  ["soa.format.date(value, 'DD/MM/YYYY')", 'a date in a pattern'],
  ['soa.warn(message)', 'a note shown with the preview'],
  ['soa.template, soa.statements, soa.mode', "the template, the statements' data, 'print' or 'preview'"],
];

const EXAMPLE_JS = `// Example: a note when the customer owes more than 50,000
soa.on('statement', function (st) {
  st.custom.note = st.totals.closingNet > 50000 ? 'Please settle the overdue balance.' : '';
});
// then print {{custom.note}} in a Text element`;

interface Props {
  css: string;
  js: string;
  onChange: (patch: { css?: string; js?: string }) => void;
}

const CodeEditor: React.FC<Props> = ({ css, js, onChange }) => (
  <Grid container spacing={2} sx={{ p: 2 }}>
    <Grid item xs={12} lg={8}>
      <Stack spacing={2}>
        <CodeBox label="CSS" value={css} onChange={(v) => onChange({ css: v })} placeholder={'.soa-row-closing .soa-td { border-top: 1.5pt solid #000; }'} rows={14} />
        <CodeBox label="JavaScript" value={js} onChange={(v) => onChange({ js: v })} placeholder={EXAMPLE_JS} rows={16} />
      </Stack>
    </Grid>
    <Grid item xs={12} lg={4}>
      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">CSS classes</Typography></AccordionSummary>
        <AccordionDetails>
          {CSS_HELP.map(([sel, what]) => (
            <Box key={sel} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{sel}</Typography>
              <Typography variant="caption" color="text.secondary">{what}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            Sizes in mm and pt print exactly. The CSS follows the generated styles, so it wins over them.
          </Typography>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle2">JavaScript</Typography></AccordionSummary>
        <AccordionDetails>
          {JS_HELP.map(([call, what]) => (
            <Box key={call} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{call}</Typography>
              <Typography variant="caption" color="text.secondary">{what}</Typography>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" component="div">
            The script runs in the statement page only: it cannot reach PRPO, the network or files. Placeholders are
            written {'{{path|format}}'}; a text line whose placeholders are all empty is left out. Errors are listed
            above the preview.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  </Grid>
);

export default CodeEditor;
